import {
  ActionEngine,
  type ActionHost,
  type ActionResult,
  type KeptStream,
  LinkError,
  type Links,
  newKeptStream,
  type Proposal,
  type QueravelArtifact,
  type QueravelEvent,
  type QueravelTurn,
  QuotaExceededError,
} from "@rhema/client";

/**
 * What goes as `message` on a request that only carries an action result. Queravel is confirming
 * whether it may be left out. Set this to "" to send an empty message instead.
 */
export const ACTION_RESULT_MESSAGE: string | undefined = undefined;

/** How often unanswered proposals are checked for the ten minute limit. */
export const EXPIRY_CHECK_MS = 1000;

/** Waits before each attempt to read a kept answer again, after the connection dropped. */
export const RESUME_DELAYS_MS = [500, 1000, 2000, 4000, 8000, 15000];

/**
 * How long an answer may go without any event before the runner reads it again from the server.
 * Queravel sends a heartbeat about every 6 seconds while a tool runs, so a quiet stream this long
 * has stalled.
 */
export const STALL_MS = 90_000;

/** A tool Queravel used or is using for the answer being written. */
export type LiveStep = { tool: string; label: string; query?: string; done: boolean };

export type QueravelStatus = "idle" | "streaming" | "error" | "quota" | "not_linked";

/**
 * The answer being written, with the question that started it. `about` is the Rhema chat the
 * question was about, when there was one.
 */
export type LiveTurn = {
  question?: string;
  label?: string;
  about?: string;
  text: string;
  references: unknown[];
  /** The tool Queravel is using right now, while it is. */
  step: LiveStep | null;
  /** Every tool Queravel used for this answer, oldest first. */
  steps: LiveStep[];
  artifacts: QueravelArtifact[];
};

/** A finished answer, ready to be kept. `question` is missing when it followed an action result. */
export type CompletedTurn = {
  question?: string;
  label?: string;
  about?: string;
  answer: string;
  references: unknown[];
  artifacts: QueravelArtifact[];
  /** The tools Queravel used, oldest first, such as web_search. */
  tools: string[];
};

export type RunnerCallbacks = {
  /** The turns so far, oldest first. Read when each request goes out. */
  history: () => QueravelTurn[];
  /** Called once an answer is complete, or stopped part way with some text. */
  onComplete: (turn: CompletedTurn) => void;
  /** Called when a question is stopped before any answer came. */
  onCancelled?: (question: string) => void;
  /** The model the person picked, read when each request goes out. Nothing means the plan default. */
  model?: () => string | undefined;
};

export type RunnerState = {
  status: QueravelStatus;
  error: string | null;
  live: LiveTurn | null;
  proposals: Proposal[];
  /** Questions asked while an answer was still being written. They go next, in order. */
  queued: { message: string; label?: string; about?: string }[];
};

/** `about` is the Rhema chat a question came from, so the server can check Queravel is allowed there. */
type Request = { message?: string; label?: string; actionResult?: ActionResult; about?: string };

export type RunnerDeps = {
  links: Links;
  host: ActionHost;
  /** Resolves once the app is on screen again. Phones pause the app while it is in the background. */
  whenVisible?: () => Promise<void>;
  sleep?: (ms: number) => Promise<void>;
  /** How long a stream may stay quiet before it counts as stalled. */
  stallMs?: number;
};

/** A stream that went quiet for too long. It is read again like a dropped connection. */
class StalledError extends Error {
  constructor() {
    super("Queravel stopped answering.");
    this.name = "StalledError";
  }
}

/** The text without its last `chars` characters, counted as Queravel counts them, by code point. */
export function dropTail(text: string, chars: number): string {
  let end = text.length;
  for (let n = 0; n < chars && end > 0; n++) {
    const low = text.charCodeAt(end - 1);
    const high = end > 1 ? text.charCodeAt(end - 2) : 0;
    end -= low >= 0xdc00 && low <= 0xdfff && high >= 0xd800 && high <= 0xdbff ? 2 : 1;
  }
  return text.slice(0, end);
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function defaultWhenVisible(): Promise<void> {
  if (typeof document === "undefined" || document.visibilityState === "visible") return Promise.resolve();
  return new Promise((resolve) => {
    const on = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", on);
      resolve();
    };
    document.addEventListener("visibilitychange", on);
  });
}

function completed(turn: LiveTurn): CompletedTurn {
  return {
    ...(turn.question !== undefined ? { question: turn.question } : {}),
    ...(turn.label ? { label: turn.label } : {}),
    ...(turn.about ? { about: turn.about } : {}),
    answer: turn.text,
    references: turn.references,
    artifacts: turn.artifacts,
    tools: turn.steps.map((s) => s.tool),
  };
}

/** Starts a step, or marks the newest unfinished one for that tool done. */
function applyStep(turn: LiveTurn, event: Extract<QueravelEvent, { type: "step" }>) {
  const { tool, label, query, phase } = event.step;
  if (phase === "call") {
    const step: LiveStep = { tool, label, ...(query ? { query } : {}), done: false };
    turn.steps = [...turn.steps, step];
    turn.step = step;
    return;
  }
  let i = turn.steps.length - 1;
  while (i >= 0 && (turn.steps[i]?.tool !== tool || turn.steps[i]?.done)) i--;
  if (i >= 0) {
    const done = { ...(turn.steps[i] as LiveStep), done: true };
    turn.steps = turn.steps.map((s, j) => (j === i ? done : s));
  }
  turn.step = null;
}

function fresh(turn: LiveTurn) {
  turn.text = "";
  turn.references = [];
  turn.step = null;
  turn.steps = [];
  turn.artifacts = [];
}

/** A network failure, as opposed to Queravel or the gateway answering with an error. */
function dropped(err: unknown): boolean {
  if (err instanceof QuotaExceededError) return false;
  if (err instanceof LinkError) return false;
  return true;
}

/**
 * One Queravel conversation. It lives above the screens, as calls do, so an answer keeps being
 * written while the person moves around the app. Every answer is also kept by the server, so when
 * the connection drops or the phone puts the app to sleep, the runner reads it again from the
 * server once it can, and nothing is lost.
 */
export class QueravelRunner {
  state: RunnerState = { status: "idle", error: null, live: null, proposals: [], queued: [] };
  readonly engine: ActionEngine;
  private listeners = new Set<() => void>();
  private callbacks: RunnerCallbacks = { history: () => [], onComplete: () => undefined };
  private busy = false;
  private abort: AbortController | null = null;
  private last: Request | null = null;
  private results: ActionResult[] = [];
  private expiry: ReturnType<typeof setInterval> | null = null;
  private deps: Required<RunnerDeps>;

  constructor(
    readonly chatId: string,
    deps: RunnerDeps,
  ) {
    this.deps = { whenVisible: defaultWhenVisible, sleep: defaultSleep, stallMs: STALL_MS, ...deps };
    this.engine = new ActionEngine(deps.host);
    this.engine.subscribe(() => {
      this.set({ proposals: this.engine.list() });
      this.watchExpiry();
      this.pump();
    });
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  /** The latest callbacks from the screen showing this conversation, if one is. */
  bind(callbacks: RunnerCallbacks) {
    this.callbacks = callbacks;
  }

  get writing(): boolean {
    return this.state.status === "streaming";
  }

  private set(patch: Partial<RunnerState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  /**
   * Asks a question. One asked while an answer is being written waits its turn. `about` names the
   * Rhema chat the question is about, when there is one.
   */
  ask(message: string, label?: string, about?: string): boolean {
    if (!message.trim()) return false;
    const req = { message, ...(label ? { label } : {}), ...(about ? { about } : {}) };
    if (this.busy) {
      this.set({ queued: [...this.state.queued, req] });
      return true;
    }
    void this.run(req);
    return true;
  }

  stop = () => {
    this.abort?.abort();
  };

  retry = () => {
    if (this.last && !this.busy) void this.run(this.last);
  };

  /** Forgets an unanswered question after an error, so the person can ask something else. */
  dismiss = () => {
    if (this.busy) return;
    this.set({ live: null, status: "idle", error: null });
  };

  /** Sends the next action result, or the next waiting question, once nothing else is writing. */
  private pump() {
    if (this.busy) return;
    this.results.push(...this.engine.takeResults());
    const result = this.results.shift();
    if (result) {
      void this.run({ actionResult: result });
      return;
    }
    const [next, ...rest] = this.state.queued;
    if (next) {
      this.set({ queued: rest });
      void this.run(next);
    }
  }

  private watchExpiry() {
    const waiting = this.state.proposals.some((p) => p.state === "pending");
    if (waiting && !this.expiry) this.expiry = setInterval(() => this.engine.expire(), EXPIRY_CHECK_MS);
    if (!waiting && this.expiry) {
      clearInterval(this.expiry);
      this.expiry = null;
    }
  }

  private async run(req: Request) {
    this.busy = true;
    this.last = req;
    const controller = new AbortController();
    this.abort = controller;
    const kept = newKeptStream();
    const turn: LiveTurn = {
      question: req.message,
      label: req.label,
      ...(req.about ? { about: req.about } : {}),
      text: "",
      references: [],
      step: null,
      steps: [],
      artifacts: [],
    };
    // Actions already handed to the engine, so reading the answer again does not propose them twice.
    const proposed = new Set<string>();
    this.set({ status: "streaming", error: null, live: { ...turn } });
    const { links, stallMs } = this.deps;
    const model = this.callbacks.model?.();

    /**
     * Reads one attempt at the answer. Each attempt has its own signal, so a stream that goes
     * quiet can be dropped and read again without stopping the answer.
     */
    const read = async (open: (signal: AbortSignal) => AsyncGenerator<QueravelEvent>) => {
      const attempt = new AbortController();
      const stop = () => attempt.abort();
      controller.signal.addEventListener("abort", stop);
      let stalled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const arm = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          stalled = true;
          attempt.abort();
        }, stallMs);
      };
      arm();
      try {
        for await (const event of open(attempt.signal)) {
          // Any event, a heartbeat too, shows the answer is still coming.
          arm();
          if (stalled) break;
          if (event.type === "text") turn.text += event.text;
          else if (event.type === "references") turn.references = event.references;
          else if (event.type === "demote") turn.text = dropTail(turn.text, event.chars);
          else if (event.type === "step") applyStep(turn, event);
          else if (event.type === "artifacts") turn.artifacts = event.artifacts;
          else if (event.type === "action") {
            const key = JSON.stringify(event.action);
            if (!proposed.has(key)) {
              proposed.add(key);
              void this.engine.propose(event.action);
            }
          } else continue;
          this.set({ live: { ...turn } });
        }
      } catch (err) {
        if (stalled && !controller.signal.aborted) throw new StalledError();
        throw err;
      } finally {
        clearTimeout(timer);
        controller.signal.removeEventListener("abort", stop);
      }
      if (stalled && !controller.signal.aborted) throw new StalledError();
    };

    try {
      try {
        await read((signal) =>
          links.queravel.stream({
            chatId: this.chatId,
            ...(req.actionResult
              ? ACTION_RESULT_MESSAGE === undefined
                ? {}
                : { message: ACTION_RESULT_MESSAGE }
              : { message: req.message ?? "" }),
            history: this.callbacks.history(),
            ...(model ? { model } : {}),
            ...(req.actionResult ? { actionResult: req.actionResult } : {}),
            signal,
            stream: kept,
            ...(req.about ? { about: req.about } : {}),
          }),
        );
      } catch (err) {
        if (controller.signal.aborted || !dropped(err)) throw err;
        await this.resume(kept, turn, controller, read);
      }
    } catch (err) {
      this.busy = false;
      this.abort = null;
      if (controller.signal.aborted) {
        void links.queravel.stop(kept);
        if (turn.text || turn.artifacts.length) this.callbacks.onComplete(completed(turn));
        else if (turn.question) this.callbacks.onCancelled?.(turn.question);
        this.set({ live: null, status: "idle" });
        this.pump();
        return;
      }
      if (err instanceof QuotaExceededError) this.set({ status: "quota", error: err.message });
      else if (err instanceof LinkError && err.code === "not_linked")
        this.set({ status: "not_linked", error: null });
      else
        this.set({
          status: "error",
          error: (err as Error)?.message || "Queravel could not answer. Try again.",
        });
      return;
    }
    this.busy = false;
    this.abort = null;
    // Keep the answer first, so it takes the live answer's place without a gap.
    // Files can come on a turn that only proposes an action, with no words.
    if (turn.text || turn.question || turn.artifacts.length) this.callbacks.onComplete(completed(turn));
    this.set({ live: null, status: "idle" });
    this.pump();
  }

  /**
   * Reads a kept answer again from the server after the connection dropped, from its start, once
   * the app is on screen. Gives up after a few tries, or once the server has let it go.
   */
  private async resume(
    kept: KeptStream,
    turn: LiveTurn,
    controller: AbortController,
    read: (open: (signal: AbortSignal) => AsyncGenerator<QueravelEvent>) => Promise<void>,
  ) {
    let lastErr: unknown = null;
    for (const delay of RESUME_DELAYS_MS) {
      await this.deps.whenVisible();
      await this.deps.sleep(delay);
      if (controller.signal.aborted) throw new DOMException("Stopped", "AbortError");
      fresh(turn);
      try {
        await read((signal) => this.deps.links.queravel.resume(kept, signal));
        return;
      } catch (err) {
        if (controller.signal.aborted) throw err;
        if (err instanceof LinkError && err.code === "not_found") throw err;
        lastErr = err;
      }
    }
    throw lastErr ?? new Error("Queravel could not answer. Try again.");
  }
}
